import type { CartMembershipBenefits } from './cart';
import { MEMBERSHIP_PATH } from './purchase';
import { isTrialEligible } from './trial';
import type { Membership } from './types';
import {
  hasMembership,
  isUnsettledPurchase,
  type LoadState,
  type MembershipActivationState,
  type MembershipView,
} from './view';

/** `?modal=` value of the Join modal: promo, card and Welcome. */
export const CLUB_JOIN_MODAL = 'club-join';

/** `?modal=` value of the "Add card" sheet of a member. */
export const CLUB_CARD_MODAL = 'club-card';

/** Query param naming the plan Join opens with. */
export const CLUB_PLAN_PARAM = 'planId';

/** Query param of a bank return; Adyen appends it to the `returnUrl`. */
export const CLUB_REDIRECT_PARAM = 'redirectResult';

/** Query param of the login modal telling it where to go once the customer is in. */
export const CLUB_AUTH_NEXT_PARAM = 'clubNext';

/** Where the login modal goes on success: the Join modal or the club page. */
export type ClubAuthNext = 'join' | 'page';

/** Reads `clubNext` of the login modal; anything unknown is `null`, the usual close. */
export const clubAfterLogin = (value: string | null): ClubAuthNext | null =>
  value === 'join' || value === 'page' ? value : null;

/** What the login modal does once the customer is in. */
export type AfterSignIn =
  /** KYC is not verified: personal data first, the club waits. */
  | { kind: 'personalData' }
  /** Join on the current page, with the plan the entry point asked for. */
  | { kind: 'join'; planId: string | null }
  /** The club page, replacing the login modal's history entry. */
  | { kind: 'page' }
  /** The usual close. */
  | { kind: 'close' };

/** Decides where the login modal goes after a sign-in; KYC wins over `clubNext`. */
export const afterSignIn = (
  isKycVerified: boolean,
  next: ClubAuthNext | null,
  planId: string | null,
): AfterSignIn => {
  if (!isKycVerified) {
    return { kind: 'personalData' };
  }
  if (next === 'join') {
    return { kind: 'join', planId: planId || null };
  }
  return next === 'page' ? { kind: 'page' } : { kind: 'close' };
};

const withParams = (params: Record<string, string | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      search.set(key, value);
    }
  }
  return `${MEMBERSHIP_PATH}?${search.toString()}`;
};

/** The club page with Join open, optionally on a plan. */
export const clubJoinPath = (planId: string | null = null): string =>
  withParams({ modal: CLUB_JOIN_MODAL, [CLUB_PLAN_PARAM]: planId });

/** The club page with "Add card" open. */
export const clubCardPath = (): string =>
  withParams({ modal: CLUB_CARD_MODAL });

const trimOrigin = (origin: string): string => origin.replace(/\/+$/, '');

/**
 * `returnUrl` of a purchase: the club page with Join open, whatever page the
 * purchase started on; the modal there finishes the payment. Adyen appends
 * `redirectResult`.
 */
export const membershipReturnUrl = (origin: string): string =>
  `${trimOrigin(origin)}${clubJoinPath()}`;

/** `returnUrl` of a card change: the club page with "Add card" open, where the sheet finishes it. */
export const membershipCardReturnUrl = (origin: string): string =>
  `${trimOrigin(origin)}${clubCardPath()}`;

/**
 * Where `/cabinet/membership/checkout/:planId` now leads: Join on the club
 * page with the plan chosen, carrying a bank's `redirectResult` along.
 */
export const legacyCheckoutRedirect = (
  planId: string,
  redirectResult: string | null,
): string =>
  withParams({
    modal: CLUB_JOIN_MODAL,
    [CLUB_PLAN_PARAM]: planId || null,
    [CLUB_REDIRECT_PARAM]: redirectResult,
  });

/** Where `/cabinet/membership/card` now leads: "Add card" on the club page, with a bank's `redirectResult`. */
export const legacyCardRedirect = (redirectResult: string | null): string =>
  withParams({ modal: CLUB_CARD_MODAL, [CLUB_REDIRECT_PARAM]: redirectResult });

const LEGACY_CARD_PATH = `${MEMBERSHIP_PATH}/card`;
const LEGACY_CHECKOUT_PREFIX = `${MEMBERSHIP_PATH}/checkout/`;

/**
 * Server-side redirect of the former card and checkout pages, so the server
 * answers them with a redirect instead of an empty page: the target of
 * `legacyCardRedirect` / `legacyCheckoutRedirect` for `url` (a path with its
 * query), carrying a bank's `redirectResult`; `null` for any other URL.
 */
export const legacyMembershipRedirect = (url: string): string | null => {
  let parsed: URL;
  let planId: string;
  try {
    parsed = new URL(url, 'http://localhost');
    const path = normalizePath(parsed.pathname);
    if (path === LEGACY_CARD_PATH) {
      return legacyCardRedirect(parsed.searchParams.get(CLUB_REDIRECT_PARAM));
    }
    if (!path.startsWith(LEGACY_CHECKOUT_PREFIX)) {
      return null;
    }
    planId = decodeURIComponent(path.slice(LEGACY_CHECKOUT_PREFIX.length));
  } catch {
    return null;
  }
  return planId && !planId.includes('/')
    ? legacyCheckoutRedirect(
        planId,
        parsed.searchParams.get(CLUB_REDIRECT_PARAM),
      )
    : null;
};

/** Pages "Continue shopping" may return to after a bank redirect: home, cart, checkout and the club page. */
export const CLUB_ORIGINS: readonly string[] = [
  '/',
  '/cart',
  '/checkout',
  MEMBERSHIP_PATH,
];

/** A pathname without its trailing slashes; the root stays `/`. The one rule every club path comparison uses. */
export const normalizePath = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : pathname;

/**
 * The page a purchase started on, as "Continue shopping" may use it: one of
 * `CLUB_ORIGINS` (a trailing slash ignored), the club page for anything else.
 */
export const clubOrigin = (pathname: string): string => {
  const path = normalizePath(pathname);
  return CLUB_ORIGINS.includes(path) ? path : MEMBERSHIP_PATH;
};

/**
 * A member whose benefits apply now, as `/me` says; `false` until `/me` is
 * loaded, so nothing member-only flashes before it.
 */
export const isClubMember = (me: LoadState<Membership | null>): boolean =>
  me.status === 'ready' && hasMembership(me.data) && me.data.benefitsActive;

/** Inputs of the cart and checkout club row. */
export interface ClubDeliveryOfferInput {
  /** Membership part of the last calculate. */
  benefits: CartMembershipBenefits;
  /** `/me` of the customer. */
  me: LoadState<Membership | null>;
  /** A purchase is being activated (polling `/me` or stalled); absent or `null` otherwise. */
  activation?: MembershipActivationState | null;
  /** Current time in ms, for the age of a `pending_card`; `Date.now()` when absent. */
  now?: number;
}

/** What the club row promises: this delivery fee would be zero. */
export interface ClubDeliveryOffer {
  /** Delivery fee the plan would save, in minor units. */
  feeMinor: number;
  /** Plan Join opens with; `null` lets Join pick the best plan. */
  planId: string | null;
  /** Reported to analytics; `null` when calculate does not name it. */
  planCode: string | null;
}

/**
 * The club row of the cart and checkout, as calculate offers it: the plan
 * would make this paid delivery free. Only for a customer with no membership
 * at all once `/me` is loaded — calculate also offers a plan to `past_due`
 * and to a member of another zone, who must not be sold a second membership.
 * Hidden while a purchase is being activated: until `/me` shows it (still 404
 * or `pending_card`) Join would invite a second payment. Hidden as well for a
 * `pending_card` that may still be charging (`isUnsettledPurchase`), started
 * elsewhere or before a reload.
 */
export const clubDeliveryOffer = ({
  benefits,
  me,
  activation,
  now = Date.now(),
}: ClubDeliveryOfferInput): ClubDeliveryOffer | null => {
  if (
    activation ||
    me.status !== 'ready' ||
    hasMembership(me.data) ||
    isUnsettledPurchase(me.data, now) ||
    benefits.kind !== 'upsell' ||
    !benefits.wouldDeliveryBeFree ||
    benefits.deliverySavingMinor <= 0
  ) {
    return null;
  }
  return {
    feeMinor: benefits.deliverySavingMinor,
    planId: benefits.planId,
    planCode: benefits.planCode,
  };
};

/**
 * Pill of the cart club row: "Try free →" when the offered plan gives this
 * customer a trial, "Join →" otherwise or when the plan is not among the
 * zone's plans.
 */
export const clubCartCtaKey = (
  view: MembershipView,
  offer: Pick<ClubDeliveryOffer, 'planId' | 'planCode'>,
): string => {
  const plans = view.kind === 'offer' ? view.plans : [];
  const plan = plans.find((item) =>
    offer.planId ? item.id === offer.planId : item.code === offer.planCode,
  );
  return plan && isTrialEligible(plan)
    ? 'membership:clubTryFreeArrow'
    : 'membership:clubJoinArrow';
};

/**
 * The cart and checkout say "Your CityDrinks Club plan doesn't cover this
 * address" instead of member benefits: a member whose plan misses the
 * current courier zone, once the zone is checked. Never for click & collect
 * (`isPickup`), where the courier zone does not matter.
 */
export const cartZoneMismatch = (
  view: MembershipView,
  isPickup: boolean,
): boolean =>
  !isPickup &&
  view.kind === 'member' &&
  view.isZoneChecked &&
  view.zoneMismatch;

/** Where a club entry point sits; reported with `Membership: Entry Viewed` and `Entry Clicked`. */
export type ClubEntrySource =
  | 'home'
  | 'cart'
  | 'checkout'
  | 'profile'
  | 'menu'
  | 'footer'
  | 'club_page';

/** Payload of `Membership: Entry Viewed` and `Membership: Entry Clicked`. */
export interface MembershipEntryPayload {
  source: ClubEntrySource;
  /** Plan the entry point advertises; `null` when it names none (menu, footer). */
  planCode: string | null;
}
